/** User-citable connection / availability codes for MiniApp overlays and support. */
export const MINIAPP_CONNECTION_ERROR_CODES = {
  DEVICE_OFFLINE: 'MA-CONN-1001',
  HEALTH_NETWORK: 'MA-CONN-1002',
  HEALTH_TIMEOUT: 'MA-CONN-1003',
  HEALTH_HTTP: 'MA-CONN-1004',
  MAINTENANCE: 'MA-CONN-1005',
  API_NETWORK: 'MA-CONN-1006',
  API_HTTP: 'MA-CONN-1007',
  SERVICE_UNAVAILABLE: 'MA-CONN-1008',
  BROWSER_OFFLINE: 'MA-CONN-1009',
  API_DEFERRED_BACKOFF: 'MA-CONN-1010',
  API_EMPTY_RESPONSE: 'MA-CONN-1011',
  UNKNOWN: 'MA-CONN-1999',
} as const;

export type MiniappConnectionErrorCode =
  (typeof MINIAPP_CONNECTION_ERROR_CODES)[keyof typeof MINIAPP_CONNECTION_ERROR_CODES];

const CODE_HINTS: Record<MiniappConnectionErrorCode, string> = {
  [MINIAPP_CONNECTION_ERROR_CODES.DEVICE_OFFLINE]: 'No network on this device.',
  [MINIAPP_CONNECTION_ERROR_CODES.HEALTH_NETWORK]: 'Could not reach Jester servers.',
  [MINIAPP_CONNECTION_ERROR_CODES.HEALTH_TIMEOUT]: 'Server health check timed out.',
  [MINIAPP_CONNECTION_ERROR_CODES.HEALTH_HTTP]: 'Server returned an error response.',
  [MINIAPP_CONNECTION_ERROR_CODES.MAINTENANCE]: 'Scheduled maintenance is in progress.',
  [MINIAPP_CONNECTION_ERROR_CODES.API_NETWORK]: 'API request could not complete.',
  [MINIAPP_CONNECTION_ERROR_CODES.API_HTTP]: 'API returned an error response.',
  [MINIAPP_CONNECTION_ERROR_CODES.SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable.',
  [MINIAPP_CONNECTION_ERROR_CODES.BROWSER_OFFLINE]: 'Browser reported offline.',
  [MINIAPP_CONNECTION_ERROR_CODES.API_DEFERRED_BACKOFF]: 'Requests paused after a connection failure — retry in a few seconds.',
  [MINIAPP_CONNECTION_ERROR_CODES.API_EMPTY_RESPONSE]: 'Server returned an empty error response.',
  [MINIAPP_CONNECTION_ERROR_CODES.UNKNOWN]: 'Connection could not be restored.',
};

export function connectionErrorHint(code: MiniappConnectionErrorCode): string {
  return CODE_HINTS[code] ?? CODE_HINTS[MINIAPP_CONNECTION_ERROR_CODES.UNKNOWN];
}

export type ConnectionErrorPresentation = {
  code: MiniappConnectionErrorCode;
  title: string;
  lead: string;
};

function normalizeText(value: string): string {
  return String(value || '').trim().toLowerCase();
}

function isMaintenanceSignal(input: {
  kind: 'maintenance' | 'disconnected';
  message: string;
  detail: string;
}): boolean {
  if (input.kind === 'maintenance') return true;
  const blob = `${input.message} ${input.detail}`.toLowerCase();
  return blob.includes('maintenance') || blob.includes('down for maintenance');
}

function isApiSignal(detail: string, source?: 'health' | 'api'): boolean {
  if (source === 'api') return true;
  const d = normalizeText(detail);
  return d.startsWith('post ') || d.includes('post /api/');
}

function isHealthSignal(detail: string, source?: 'health' | 'api'): boolean {
  if (source === 'health') return true;
  const d = normalizeText(detail);
  return d.startsWith('get ') || d.includes('/health');
}

/** Map health / API diagnostics to a stable support code + user copy. */
export function resolveConnectionErrorPresentation(input: {
  kind: 'maintenance' | 'disconnected';
  isOffline: boolean;
  message: string;
  detail: string;
  diagnosticSource?: 'health' | 'api';
}): ConnectionErrorPresentation {
  const detail = String(input.detail || '').trim();
  const detailLower = normalizeText(detail);
  const message = String(input.message || '').trim();

  if (input.isOffline) {
    if (detailLower.includes('offline event') || detailLower.includes('window "offline"')) {
      return {
        code: MINIAPP_CONNECTION_ERROR_CODES.BROWSER_OFFLINE,
        title: 'Connection lost',
        lead: "You're offline. We'll reconnect automatically when your network returns.",
      };
    }
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.DEVICE_OFFLINE,
      title: 'Connection lost',
      lead: "You're offline. We'll reconnect automatically when your network returns.",
    };
  }

  if (isMaintenanceSignal(input)) {
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.MAINTENANCE,
      title: message && !/^down for maintenance$/i.test(message) ? message : 'Scheduled maintenance',
      lead: 'Jester is briefly unavailable while we upgrade the service.',
    };
  }

  if (detailLower.includes('timed out')) {
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.HEALTH_TIMEOUT,
      title: 'Connection issue',
      lead: "Jester is taking longer than usual to respond. We're reconnecting automatically.",
    };
  }

  const fromApi = isApiSignal(detail, input.diagnosticSource);
  const fromHealth = isHealthSignal(detail, input.diagnosticSource);

  if (detailLower.includes('failed to fetch') || detailLower.includes('network error') || detailLower.includes('connection refused')) {
    if (fromApi) {
      return {
        code: MINIAPP_CONNECTION_ERROR_CODES.API_NETWORK,
        title: 'Connection issue',
        lead: "Jester could not complete a background request. We're reconnecting automatically.",
      };
    }
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.HEALTH_NETWORK,
      title: 'Connection issue',
      lead: "Jester could not reach the server. We're reconnecting automatically.",
    };
  }

  if (/\bhttp\s+\d{3}\b/i.test(detail) || detailLower.includes('→ http')) {
    if (fromApi) {
      return {
        code: MINIAPP_CONNECTION_ERROR_CODES.API_HTTP,
        title: 'Service temporarily unavailable',
        lead: "A Jester API request failed. We're reconnecting automatically.",
      };
    }
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.HEALTH_HTTP,
      title: 'Service temporarily unavailable',
      lead: "The server reported an error. We're reconnecting automatically.",
    };
  }

  if (fromApi) {
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.API_NETWORK,
      title: 'Connection issue',
      lead: "Jester could not complete a background request. We're reconnecting automatically.",
    };
  }

  if (fromHealth || input.kind === 'disconnected') {
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.HEALTH_NETWORK,
      title: 'Connection issue',
      lead: "Jester could not reach the server. We're reconnecting automatically.",
    };
  }

  if (/service temporarily unavailable/i.test(message)) {
    return {
      code: MINIAPP_CONNECTION_ERROR_CODES.SERVICE_UNAVAILABLE,
      title: 'Service temporarily unavailable',
      lead: "Jester is temporarily unavailable. We're reconnecting automatically.",
    };
  }

  return {
    code: MINIAPP_CONNECTION_ERROR_CODES.UNKNOWN,
    title: message || 'Connection issue',
    lead: "Jester is reconnecting automatically.",
  };
}
